import { type IReactionDisposer, makeAutoObservable, runInAction } from 'mobx'
import { Box, type IDropdownItem, Status, Typography } from 'shared/ui'
import { logger } from 'shared/lib/logger'
import { getUrlEndPath } from 'shared/lib'
import { type Integration, IntegrationKey } from 'entities/Integrations'
import {
  ChatbotBookingType,
  type IAgentSecondStepPayload,
  type IBookingType,
  type IChatbotFullData,
} from 'entities/Chatbot'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ManageIntegrationConnectionStore } from 'features/ManageIntegrationConnection'
import { MeetingsStore } from 'widgets/Meetings/store/meetingsStore'
import styles from 'pages/chatbot/pages/agentBookAppointment/pages/connectCalendar/ui/styles.module.scss'
import type { AgentBookAppointmentStepsStore } from 'pages/chatbot/pages/agentBookAppointment/store/agentBookAppointmentStepsStore'
import { type IHandleError } from 'pages/chatbot/pages/agentBookAppointment/store/type'
import { type ICalendlyIntegrationMeetingCustom } from 'pages/chatbot/pages/agentBookAppointment/pages/connectCalendar/store/types'

export type IEventLinkDropdownItem = IDropdownItem & {
  data: ICalendlyIntegrationMeetingCustom
}

export class ConnectCalendarStore {
  private _bookingType: IBookingType = ChatbotBookingType.TimeChat
  private _selectedIntegration: Integration | null = null
  private _isLoading = false
  private _integrationMap: Map<number | string, Integration> = new Map()
  private _eventLink: IEventLinkDropdownItem | null = null
  private _errorsMap: Map<string, string> = new Map()
  private _disposeFindEventLink: IReactionDisposer | null = null

  private _manageIntegrationConnectionStore = new ManageIntegrationConnectionStore()
  private _meetingsStore: MeetingsStore
  private _loading = false

  constructor(
    private _stepsStore: AgentBookAppointmentStepsStore,
    private _entities: IEntitiesStore
  ) {
    this._meetingsStore = new MeetingsStore({ contact: null }, this._entities)

    makeAutoObservable(this)
  }

  get disabled() {
    return this._loading
  }

  get stepsStoreLoading() {
    return this._stepsStore.loading
  }

  get agentActionsStore() {
    return this._stepsStore.agentActionsStore
  }

  get bookingType() {
    return this._bookingType
  }

  get isConnectionSelected() {
    return !!this._selectedIntegration
  }

  get isCalendlySelected() {
    return this._selectedIntegration?.key === IntegrationKey.calendly
  }

  get isHubspotSelected() {
    return this._selectedIntegration?.key === IntegrationKey.hubspot
  }

  get selectedConnectionItem(): IDropdownItem | null {
    if (!this._selectedIntegration) return null
    return this.connectionList.find((item) => item.id === this._selectedIntegration?.id) ?? null
  }

  get isLoading() {
    return this._isLoading
  }

  get errorsMap() {
    return this._errorsMap
  }

  get isMeetingLoading() {
    return this._meetingsStore.loading
  }

  get search() {
    return this._meetingsStore.search
  }

  get hasMoreMeetings() {
    return this._meetingsStore.hasMoreMeetings
  }

  get isLoadingMoreMeetings() {
    return this._meetingsStore.isLoadingMoreMeetings
  }

  loadMoreMeetings = () => {
    void this._meetingsStore.loadMoreMeetings()
  }

  get eventLink() {
    return this._eventLink
  }

  get bookingTypes() {
    return [
      {
        id: ChatbotBookingType.TimeChat,
        customLabel: (
          <div className={styles.customLabel}>
            <Box display={'flex'} alignItems={'baseline'} gap={1}>
              <Typography variant={'body-md-medium'} ariaLabel={'Chat'}>
                Chat to book a time
              </Typography>
            </Box>
            <Typography
              variant={'text-input-field-r'}
              ariaLabel={'Chat_description'}
              color={'var(--content-primary-disabled)'}
            >
              The AI Agent will text back and forth with the customer to find a time
            </Typography>
          </div>
        ),
      },
      {
        id: ChatbotBookingType.ShareLink,
        disabled: this.isHubspotSelected,
        customLabel: (
          <div className={styles.customLabel}>
            <Box display={'flex'} alignItems={'baseline'} gap={1}>
              <Typography variant={'body-md-medium'} ariaLabel={'link'}>
                Share booking link
              </Typography>
              {this.isHubspotSelected && (
                <Status title={'Coming soon'} intent={'neutral'} emphasis={'high'} size={'label'} />
              )}
            </Box>
            <Typography
              variant={'text-input-field-r'}
              ariaLabel={'Link_description'}
              color={'var(--content-primary-tertiary)'}
            >
              The AI Agent will share the booking link for the customer to find a time
            </Typography>
          </div>
        ),
      },
    ]
  }

  get connectionList(): IDropdownItem[] {
    const list: IDropdownItem[] = []

    this._entities.integrationsStore
      .getIntegrations([IntegrationKey.calendly, IntegrationKey.hubspot])
      .forEach((item) => {
        this._integrationMap.set(item.id, item)
        const isCalendly = item.key === IntegrationKey.calendly

        const label = item.name

        list.push({
          id: item.id,
          iconL: isCalendly ? 'calendly' : 'hubspot',
          label,
          labelRight: item.connected ? null : 'Connect',
          labelRightVariant: 'body-md-regular',
          labelContent: (
            <Box display={'flex'} alignItems={'center'} gap={1}>
              <Typography variant={'body-md-regular'} ariaLabel={'hubspot'}>
                {label}
              </Typography>
              {!isCalendly && (
                <Status title={'New'} intent={'purple'} emphasis={'high'} size={'label'} />
              )}
            </Box>
          ),
        })
      })
    return list
  }

  get meetingsList(): IDropdownItem[] {
    const meetingList: IDropdownItem[] = []

    this._meetingsStore.filteredMeetings?.forEach((meeting) => {
      const calendarEvent: ICalendlyIntegrationMeetingCustom = {
        name: meeting.name,
        scheduling_url: 'scheduling_url' in meeting ? meeting.scheduling_url : '',
        id: meeting.id,
      }
      const isConfigurationValid =
        'is_configuration_valid' in meeting ? meeting?.is_configuration_valid : false
      const labelIntegration = this.isCalendlySelected ? 'Calendly' : 'HubSpot'

      meetingList.push({
        id: meeting.id,
        label: calendarEvent.name,
        labelRight: getUrlEndPath(calendarEvent.scheduling_url),
        labelRightVariant: 'body-md-regular',
        data: calendarEvent,
        labelRightClassName: styles.labelRight,
        ...(!isConfigurationValid && {
          disabled: true,
          tooltipProps: {
            label: (
              <>
                {`This link is missing required fields in ${labelIntegration}`}.
                <br />
                {`Update it in ${labelIntegration} to use.`}
              </>
            ),
            placement: 'top',
            breakWord: true,
          },
        }),
      })
    })

    return meetingList
  }

  get stepPayload(): IAgentSecondStepPayload {
    return {
      settings: {
        book_appointment_type: { value: this._bookingType },
        smart_booking_enabled: { value: this._smart_booking_enabled },
        book_appointment_link: {
          value: this._eventLink?.data.scheduling_url || '',
          id: this._eventLink?.data.id || '',
          name: this._eventLink?.data.name || '',
        },
        booking_provider: this._selectedIntegration
          ? { value: this._selectedIntegration.key }
          : null,
      },
    }
  }

  get isChatBookingType() {
    return this._bookingType === ChatbotBookingType.TimeChat
  }

  get bookingFieldsStore() {
    return this._stepsStore.bookingFieldsStore
  }

  get selectedIntegrationKey() {
    return this._selectedIntegration?.key ?? null
  }

  init = async (data: IChatbotFullData | null) => {
    if (!data) return

    const {
      book_appointment_type,
      book_appointment_link,
      smart_booking_enabled,
      booking_provider,
    } = data.settings ?? {}

    this.setBookingType(book_appointment_type?.value ?? 'share_link')
    this.setSmartBookingEnabled(smart_booking_enabled?.value ?? true)
    this._eventLink = book_appointment_link
      ? {
          id: book_appointment_link.id,
          label: book_appointment_link.name,
          labelRight: getUrlEndPath(book_appointment_link?.value || ''),
          labelRightVariant: 'body-md-regular',
          data: {
            ...book_appointment_link,
            scheduling_url: book_appointment_link?.value,
          },
        }
      : null

    if (book_appointment_link?.value) {
      const integrationKey =
        booking_provider?.value ?? this._getSavedIntegrationKey(book_appointment_link.value)
      const integration = this._entities.integrationsStore
        .getIntegrations([IntegrationKey.calendly, IntegrationKey.hubspot])
        .find((integration) => integration.key === integrationKey && integration.connected)

      if (integration) {
        this._selectedIntegration = integration
        this._integrationMap.set(integration.id, integration)
        this._meetingsStore.onSelectedIntegration(
          integration,
          integration.key !== IntegrationKey.calendly
        )
        if (
          integration.key === IntegrationKey.hubspot &&
          this._bookingType === ChatbotBookingType.ShareLink
        ) {
          this._bookingType = ChatbotBookingType.TimeChat
        }
      }
    }
  }

  setSearch = (search: string) => {
    this._meetingsStore.setSearch(search)
  }

  setConnection = async (connection: IDropdownItem) => {
    const integration = this._integrationMap.get(connection.id)

    if (!integration) return

    if (!integration.connected) {
      try {
        runInAction(() => {
          this._isLoading = true
        })

        await this._manageIntegrationConnectionStore.connect(integration)
      } catch (e) {
        logger.error(e)
      } finally {
        runInAction(() => {
          this._isLoading = false
        })
      }
      return
    }

    if (this._selectedIntegration?.id === integration.id) return

    runInAction(() => {
      if (this._selectedIntegration) {
        this._eventLink = null
        this._entities.mergeFieldStore.removeDynamicMergeFields(
          this._selectedIntegration.key as IntegrationKey
        )
        this.bookingFieldsStore.dispose()
      }
      this._selectedIntegration = integration
      if (
        integration.key === IntegrationKey.hubspot &&
        this._bookingType === ChatbotBookingType.ShareLink
      ) {
        this._bookingType = ChatbotBookingType.TimeChat
      }
    })

    this._meetingsStore.onSelectedIntegration(
      integration,
      integration.key !== IntegrationKey.calendly
    )
  }

  setLink = (link: IDropdownItem) => {
    this._eventLink = link as IEventLinkDropdownItem

    const integrationKey = this.selectedIntegrationKey
    if (integrationKey) {
      void this.bookingFieldsStore.fetchFields({
        params: { id: String(this._eventLink.id), integrationKey },
      })
    }
  }

  setError = (key: string, error: string) => {
    this._errorsMap.set(key, error)
  }

  removeError = (key: string) => {
    this._errorsMap.delete(key)
  }

  setBookingType = (bookingType: IBookingType) => {
    this._bookingType = bookingType
  }

  setConnectionError = () => {
    this.setError('connection', 'Select a scheduling tool to connect your calendar.')
  }

  checkValidate = () => {
    let validate = true
    if (!this.isConnectionSelected) {
      this.setConnectionError()
      validate = false
    }

    if (!this._eventLink) {
      this.setError('book_appointment_link', 'Select an event to share with contacts.')
      validate = false
    }

    return validate
  }

  handleError = (errors: IHandleError) =>
    Object.entries(errors).forEach(([errorKey, errorMessages]) => {
      const processedErrorKey = errorKey.includes('.') ? errorKey.split('.')[1] : errorKey

      if (!this.isConnectionSelected && processedErrorKey === 'book_appointment_link') {
        return this.setConnectionError()
      }

      this.setError(
        processedErrorKey,
        Array.isArray(errorMessages) ? errorMessages[0] : errorMessages
      )
    })

  handleSaveDraft = async (id?: string, shouldRedirect = true) => {
    try {
      if (this._loading) return false

      runInAction(() => {
        this._loading = true
      })

      await this._stepsStore.quickSaveDraft('step_2', { shouldRedirect, id: Number(id) })
      return true
    } catch (e) {
      this._errorsMap.clear()
      this._stepsStore.handleApiError(e, (errors) => this.handleError(errors))
      return false
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  handleNextClick = async (id?: string) => {
    if (!this.checkValidate()) return

    if (this._loading) return false

    runInAction(() => {
      this._loading = true
    })

    try {
      await this._stepsStore.handleStepValidationAndNavigation('step_2', this.stepPayload, id)
    } catch (e) {
      this._stepsStore.handleValidationApiError(e, this.handleError)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  dispose = () => {
    this._disposeFindEventLink?.()
  }

  private _smart_booking_enabled = true
  setSmartBookingEnabled = (isEnabled: boolean) => {
    this._smart_booking_enabled = isEnabled
  }

  get smartBookingEnabled() {
    return this._smart_booking_enabled
  }

  private _getSavedIntegrationKey = (url: string) => {
    if (url.includes('calendly.com')) return IntegrationKey.calendly
    return IntegrationKey.hubspot
  }
}
